import Vue from "vue";
import {
    FetchState,
    ExpenseBalanceSummaryState,
    ExpenseBalanceSummary,
    ExpenseReimburseMeParams,
    ExpenseEligibility,
    ReimbursementRequest
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as expenseService from "@/shared/api/expenseService";
import { defineStore } from "pinia";
import _ from "lodash";
import { useExpenseUnreimbursedStore } from "@/employee/store/expenseUnreimbursed";

// this guy is really like the GreenBar / Tab Totals store

export const initialState: ExpenseBalanceSummaryState = {
    fetchState: FetchState.Uninitialized,
    likelyEligible: 0,
    potentiallyEligible: 0,
    unlikelyEligible: 0,
    ineligible: 0,
    reimbursed: 0,
    reimbursedYtd: 0,
    unreimbursed: 0,
    reimburseMeBar: []
};

export const useExpenseBalanceSummaryStore = defineStore("expenseBalanceSummary", {
    state: (): ExpenseBalanceSummaryState => (_.cloneDeep(initialState)),
    getters: {
        isSummaryLoaded: state => state.fetchState === FetchState.Loaded,
        likelyEligibleBalance: state => state.likelyEligible,
        potentiallyEligibleBalance: state => state.potentiallyEligible,
        unlikelyEligibleBalance: state => state.unlikelyEligible,
        ineligibleBalance: state => state.ineligible,
        reimburseMeAmount: state => {
            let amount = 0;
            const reimbursementLinkedAccountId = 0;
            const expenseIds: number [] = [];

            for (const params of state.reimburseMeBar) {
                if (params.eligibility === ExpenseEligibility.Eligible) {
                    amount += params.amount;
                    expenseIds.push(params.expenseId);
                }
            }
            const reimburseRequest: ReimbursementRequest = {
                amount,
                reimbursementLinkedAccountId,
                expenseIds
            };
            return reimburseRequest;
        },
        reimburseMeIsVisible: state => {
            for (const params of state.reimburseMeBar) {
                if (params.eligibility === ExpenseEligibility.Eligible) {
                    return true;
                }
            }

            return false;
        }
    },
    actions: {
        setFetchState (fetchState: FetchState): void {
            this.fetchState = fetchState;
        },
        setBalances (data: ExpenseBalanceSummary): void {
            this.likelyEligible = data.likelyEligible;
            this.potentiallyEligible = data.potentiallyEligible;
            this.unlikelyEligible = data.unlikelyEligible;
            this.ineligible = data.ineligible;
            this.unreimbursed = data.unreimbursed;
            this.reimbursed = data.reimbursed;
            this.reimbursedYtd = data.reimbursedYtd;
        },
        addReimburseMeAmount (params: ExpenseReimburseMeParams) {
            const len = this.reimburseMeBar.length;
            for (let index = 0; index < len; index++) {
                const ps = this.reimburseMeBar[index];
                if (ps && ps.expenseId === params.expenseId) {
                    Vue.set(this.reimburseMeBar, index, params);
                    return;
                }
            }

            Vue.set(this.reimburseMeBar, len, params);
        },
        removeReimburseMeAmount (expenseId: number) {
            const len = this.reimburseMeBar.length;
            for (let index = 0; index < len; index++) {
                const ps = this.reimburseMeBar[index];
                if (ps && ps.expenseId === expenseId) {
                    Vue.delete(this.reimburseMeBar, index);
                }
            }

            return;
        },
        updateReimburseMeAmount (params: ExpenseReimburseMeParams) {
            const item = this.reimburseMeBar.find(e => e.expenseId === params.expenseId);
            if (item) {
                item.amount = params.amount;
            }
            return;
        },
        loadState (): Promise<any> {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            if (this.fetchState !== FetchState.Loaded) {
                this.retrieveBalanceSummary();
            }
        },
        retrieveBalanceSummary (): Promise<any> {
            if (this.fetchState !== FetchState.Loading) {
                this.setFetchState(FetchState.Loading);
                expenseService.getBalanceSummary().then(response => {
                    this.setFetchState(FetchState.Loaded);
                    this.setBalances(response.data);
                }).catch(err => {
                    this.setFetchState(FetchState.Loaded);
                    storeHelper.handleError({ error: err, rethrow: true });
                });
                return Promise.resolve();
            }
        },
        reimburseMeDismiss () {
            this.reimburseMeBar = [];
            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            expenseUnreimbursedStore.reloadPage();
        }
    }
});